import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Inject } from 'vue-di-container';
import WithRender from './TemplateFilterCheck.html';
import TemplateFilterCheckService from './TemplateFilterCheckService';
import { TemplateFilterCheckResultModel, TemplateFilterCheckSenderModel } from './TemplateFilterCheckModel';
import { Toaster } from '../../Services/toast';

@WithRender
@Component({
    components: {
    },
    diProvide: [
        TemplateFilterCheckService,
    ],
})
export default class TemplateFilterCheck extends Vue {
    @Inject(TemplateFilterCheckService) public templateFilterCheckService!: TemplateFilterCheckService;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    /** The names currently loaded for the active query: replaced on page 0, appended to after. */
    public senders: TemplateFilterCheckSenderModel[] = [];
    public selectedSender: string = '';
    public message: string = '';

    /** Bound to the autocomplete's search box. Null is what Vuetify puts there when it is cleared. */
    public senderSearch: string | null = null;

    /**
     * The chosen name, kept separately so it survives a search that no longer contains it. Without this
     * the picker would blank its own selection as soon as the user typed something else.
     */
    public selectedSenderItem: TemplateFilterCheckSenderModel | null = null;

    public hasMoreSenders: boolean = false;

    /**
     * The limit the API will enforce, as the API reported it. Null until the sender list loads, which is
     * why submitting is blocked until then - the page deliberately has no limit of its own to fall back on.
     */
    public maxMessageLength: number | null = null;

    public result: TemplateFilterCheckResultModel | null = null;

    /** Per-attempt error. Cleared whenever an input changes. */
    public errorCode: string = '';

    /**
     * Page-level error from loading the sender list. Survives typing - clearing it left the page dead
     * and silent, with an empty dropdown and no explanation. Only a retry clears it.
     */
    public loadErrorCode: string = '';

    /**
     * A failure while searching or paging AFTER the page has started. Separate from loadErrorCode: that
     * one means the page never became usable, this one means the current query failed and can be retried
     * while the rest of the page still works.
     */
    public senderSearchErrorCode: string = '';
    public loadingSenders: boolean = false;
    public validating: boolean = false;

    /**
     * Guards against a late response overwriting a newer one: every submission takes a ticket, and a
     * reply is only applied if its ticket is still the current one.
     */
    private requestTicket: number = 0;
    private destroyed: boolean = false;

    /**
     * Separate from requestTicket, which guards submissions. This one guards SEARCHES: it is bumped the
     * moment the query changes, before the debounce, so a reply for a query the user has already typed
     * past can never replace the names, the paging cursor or the error.
     */
    private senderGeneration: number = 0;
    private senderRequestActive: boolean = false;
    private nextSenderPage: number = 0;
    private activeQuery: string = '';
    private searchTimer: any = null;
    private menuContent: HTMLElement | null = null;
    private onMenuScroll: any = null;

    /** The shell keeps the previous page's layout width without this emit. */
    public async created() {
        this.$emit('childinit', this.layoutSup);
    }

    public async mounted() {
        await this.loadSenders();

        // Vuetify 1.5 registers an update:menu listener but never emits it - that event arrived in
        // Vuetify 2 - so the menu open state has to be watched directly. Without this the scroll
        // listener is never attached and the list simply stops at the first page.
        this.$nextTick(() => {
            const picker: any = this.$refs.senderPicker;
            if (picker) {
                this.$watch(() => picker.isMenuActive, (open: boolean) => this.onPickerMenu(open));
            }
        });
    }

    public beforeDestroy() {
        this.destroyed = true;
        this.senderGeneration++;
        if (this.searchTimer !== null) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        this.detachMenuScroll();
    }

    /**
     * Validate needs a loaded limit, a chosen sender and non-blank content. Deliberately NOT blocked by
     * loadingSenders: once a sender is chosen, a background search or a page fetch is no reason to stop
     * the user submitting.
     */
    public get canValidate(): boolean {
        return !this.validating
            && this.maxMessageLength !== null
            && !!this.selectedSender
            && this.message.trim().length > 0;
    }

    /**
     * What the dropdown shows: the current page of results, plus the chosen name if the active query no
     * longer returns it, so the field keeps displaying what the user picked.
     */
    public get pickerItems(): TemplateFilterCheckSenderModel[] {
        const items = this.senders.slice();
        const chosen = this.selectedSenderItem;
        if (chosen && !items.some((s) => s.senderName === chosen.senderName)) {
            items.unshift(chosen);
        }
        return items;
    }

    public get hasSenderSearchError(): boolean {
        return this.senderSearchErrorCode !== '';
    }

    public get senderSearchErrorText(): string {
        return this.$t('templateFilterCheck.errors.' + (this.senderSearchErrorCode || 'unavailable')).toString();
    }

    /** Distinguishes "nothing matches what you typed" from "you have no senders at all". */
    public get noSendersText(): string {
        return this.activeQuery === ''
            ? this.$t('templateFilterCheck.noSenders').toString()
            : this.$t('templateFilterCheck.noMatchingSenders').toString();
    }

    public get hasResult(): boolean {
        return this.result !== null;
    }

    public get hasError(): boolean {
        return this.errorCode !== '';
    }

    public get hasLoadError(): boolean {
        return this.loadErrorCode !== '';
    }

    /** `max` is supplied for every code; the messages that do not mention a limit simply ignore it. */
    public get errorText(): string {
        return this.$t(
            'templateFilterCheck.errors.' + (this.errorCode || 'unavailable'),
            { max: this.maxMessageLength }).toString();
    }

    public get loadErrorText(): string {
        return this.$t('templateFilterCheck.errors.' + (this.loadErrorCode || 'unavailable')).toString();
    }

    /** Renders the 0..1 upstream ratio as a percentage. Zero is a real score, not a missing one. */
    public get similarityText(): string {
        const value = this.result && this.result.similarity;
        if (value === null || value === undefined) {
            return this.$t('templateFilterCheck.notAvailable').toString();
        }
        return (Math.round(value * 10000) / 100).toString() + '%';
    }

    public get matchedTemplateText(): string {
        if (!this.result || !this.result.matchedTemplate) {
            return this.$t('templateFilterCheck.noMatchedTemplate').toString();
        }
        return this.result.matchedTemplate;
    }

    /** Upstream reports a raw .NET TimeSpan; show milliseconds rather than leaking that format. */
    public get timeText(): string {
        const raw = this.result && this.result.time;
        if (!raw) {
            return this.$t('templateFilterCheck.notAvailable').toString();
        }
        const parts = /^(\d+):(\d+):(\d+(?:\.\d+)?)$/.exec(raw);
        if (!parts) {
            return raw;
        }
        const ms = ((Number(parts[1]) * 3600) + (Number(parts[2]) * 60) + Number(parts[3])) * 1000;
        return (ms < 1 ? ms.toFixed(3) : ms.toFixed(2)) + ' ms';
    }

    @Watch('selectedSender')
    public onSenderChanged(value: string) {
        this.onSenderSelected(value);
        this.clearResult();
    }

    @Watch('message')
    public onMessageChanged() {
        this.clearResult();
    }

    /**
     * First load, and the page-level retry. A failure here is page-level: the limit never arrives, so the
     * page cannot submit at all and the error must outlive the next keystroke.
     */
    public async loadSenders() {
        this.selectedSender = '';
        this.selectedSenderItem = null;
        this.senderSearch = null;
        this.maxMessageLength = null;
        this.loadErrorCode = '';
        await this.startSearch('', true);
    }

    /** Called by the autocomplete as the user types. */
    @Watch('senderSearch')
    public onSenderSearchChanged(value: string | null) {
        const query = (value || '').trim();

        // Selecting an item makes Vuetify write that item's text into the search box. Re-running the
        // search for it would throw away the results the user is looking at.
        if (this.selectedSenderItem && query === this.selectedSenderItem.senderName) {
            return;
        }
        if (query === this.activeQuery) {
            return;
        }

        // Bumped BEFORE the debounce, so a reply already in flight for the previous query is dead the
        // instant the user types, not 300 ms later.
        this.senderGeneration++;
        if (this.searchTimer !== null) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => {
            this.searchTimer = null;
            this.startSearch(query, false);
        }, 300);
    }

    /** Replaces the results with page 0 of a query. */
    public async startSearch(query: string, bootstrap: boolean) {
        const generation = ++this.senderGeneration;
        this.activeQuery = query;
        this.senders = [];
        this.hasMoreSenders = false;
        this.nextSenderPage = 0;
        this.senderSearchErrorCode = '';
        await this.fetchSenderPage(generation, query, 0, bootstrap);
    }

    /** Appends the next page. Called by the menu's scroll listener and by the footer retry. */
    public async loadMoreSenders() {
        if (this.senderRequestActive || !this.hasMoreSenders || this.hasSenderSearchError) {
            return;
        }
        await this.fetchSenderPage(this.senderGeneration, this.activeQuery, this.nextSenderPage, false);
    }

    /**
     * The single request path. Everything it writes is guarded by its generation, so a reply for a
     * superseded query cannot touch the items, the cursor, the limit or the error.
     */
    private async fetchSenderPage(
        generation: number, query: string, pageIndex: number, bootstrap: boolean) {
        if (this.senderRequestActive) {
            return;
        }
        this.senderRequestActive = true;
        this.loadingSenders = true;

        const outcome = await this.templateFilterCheckService.searchSenders(query, pageIndex, 20);

        if (this.destroyed || generation !== this.senderGeneration) {
            this.senderRequestActive = false;
            return;
        }
        this.senderRequestActive = false;
        this.loadingSenders = false;

        if (!outcome.ok || !outcome.data) {
            const code = outcome.errorCode || 'unavailable';
            if (bootstrap || this.maxMessageLength === null) {
                this.loadErrorCode = code;
                Toaster.error(this.loadErrorText);
            } else {
                // Not a toast: this fires per keystroke and per scroll, and a stream of toasts for a
                // flaky connection is worse than the failure. It is shown inside the dropdown instead.
                this.senderSearchErrorCode = code;
            }
            return;
        }

        const page = outcome.data;
        this.maxMessageLength = page.maxMessageLength;
        const names = page.senders || [];
        if (pageIndex === 0) {
            this.senders = names;
        } else {
            // Defensive: offset paging over live data can repeat a name if rows shift between pages.
            const seen: any = {};
            this.senders.forEach((item) => { seen[item.senderName] = true; });
            names.forEach((item) => {
                if (!seen[item.senderName]) {
                    seen[item.senderName] = true;
                    this.senders.push(item);
                }
            });
        }
        this.hasMoreSenders = page.hasMore;
        this.nextSenderPage = page.pageIndex + 1;
    }

    /** Remembers the chosen name so it survives later searches. */
    public onSenderSelected(value: string) {
        if (!value) {
            this.selectedSenderItem = null;
            return;
        }
        const match = this.pickerItems.filter((item) => item.senderName === value);
        this.selectedSenderItem = match.length > 0 ? match[0] : { senderName: value };
    }

    /**
     * Vuetify 1.5 has no v-intersect, and the dropdown is rendered detached from this component, so the
     * only place to watch is the menu's own content element. It is created lazily when the menu first
     * opens, hence the retry on the next tick.
     */
    public onPickerMenu(open: boolean) {
        if (!open) {
            this.detachMenuScroll();
            return;
        }
        this.$nextTick(() => {
            const picker: any = this.$refs.senderPicker;
            const menu = picker && picker.$refs ? picker.$refs.menu : null;
            const content = menu && menu.$refs ? menu.$refs.content : null;
            if (!content) {
                return;
            }
            this.detachMenuScroll();
            this.menuContent = content;
            this.onMenuScroll = () => {
                const el = this.menuContent;
                if (!el) {
                    return;
                }
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
                    this.loadMoreSenders();
                }
            };
            content.addEventListener('scroll', this.onMenuScroll);
        });
    }

    private detachMenuScroll() {
        if (this.menuContent && this.onMenuScroll) {
            this.menuContent.removeEventListener('scroll', this.onMenuScroll);
        }
        this.menuContent = null;
        this.onMenuScroll = null;
    }

    /** Submits the current sender and message, ignoring any reply that a newer submission supersedes. */
    public async validate() {
        if (!this.canValidate) {
            return;
        }

        this.clearResult();
        this.validating = true;
        const ticket = ++this.requestTicket;

        const outcome = await this.templateFilterCheckService.validate(this.selectedSender, this.message);

        if (this.destroyed || ticket !== this.requestTicket) {
            return;
        }
        this.validating = false;

        if (!outcome.ok) {
            if (outcome.errorCode === 'messageTooLong') {
                // The server enforced a limit this page may no longer hold: an operator can lower it and
                // restart the API while this tab stays open, and the limit is otherwise read once at mount.
                // Take the current one before naming a number the user can check against their own text.
                // One row, for its limit only: this must not disturb the picker's items or selection.
                const refreshed = await this.templateFilterCheckService.searchSenders('', 0, 1);
                if (this.destroyed || ticket !== this.requestTicket) {
                    return;
                }
                if (refreshed.ok && refreshed.data) {
                    this.maxMessageLength = refreshed.data.maxMessageLength;
                }
            }
            this.errorCode = outcome.errorCode || 'unavailable';
            Toaster.error(this.errorText);
            return;
        }

        this.result = outcome.data;
        if (this.result && this.result.valid) {
            Toaster.success(this.$t('templateFilterCheck.validToast').toString());
        }
    }

    /** Clears the per-attempt state only; a page-level load error survives. */
    private clearResult() {
        this.result = null;
        this.errorCode = '';
    }
}
